/**
 * Incomplete Transfer service
 */
import { Router } from '@angular/router';
import { Injectable, Injector } from '@angular/core';
import { NgRedux, select } from '@angular-redux/store';
import { ColDef, CellClassParams } from 'ag-grid-community';
import {
  head,
  isEqual,
  isNil,
  some,
  isEmpty,
  size,
  get,
  map,
  includes,
  merge,
  last,
  split,
  nth,
} from 'lodash';
import { Observable, BehaviorSubject, of, Subject } from 'rxjs';
import { detectMobile } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';

import { IAppState } from '@app/store';
import { XUXListContainerComponent } from '@app/containers/base';
import { EventsActions, UiActions, XprtDataActions } from '@app/store/actions';
import { lazyInject } from '@app/containers/base/decorators';
import {
  IEntityConfig,
  ENTITY_TYPES,
  CELL_RENDERER,
  TRANSFER_TYPE,
  CELL_FILTER,
  IProcessedEvent,
  IModalDialogResult,
  IBaseEntityType,
  IFilterQuery,
  IDocumentOperationOptions,
  ITransfer,
  XUX_GRID_ACTIONS,
  XuxGridAction,
  DocumentOperationHeaders,
  EHeaderName,
  IRequestOptions,
  IEvent,
  FilterOptionType,
  TRANSFER_FILTER_STATUS_OPTIONS,
  TRANSFER_STATUS_ENUM_OPTIONS,
} from '@shared/types';
import { rx, observableCombineLatest } from '@app/shared/rx';
import { EVENT_STATUS_VALUE } from '@app/shared/types/enums/event-status.enum';
import { getSingularEntityName } from '@app/shared/configs';
import { TRANSFER_STATUS_VALUE } from '@app/shared/types/enums/transfer-status.enum';
import { BTN_EDIT, BTN_SELECT_EVENT } from '@app/text-content/button-templates';
import { CANCEL_ACTION, COLUMN_SELECTOR_ID } from '@config/index';

import { BaseService } from '../base';
import { transferEntityType } from '../transfer/transfer.service.config';
import { transferTypeFormatter } from '../transfer/transfer.util';
import { TransferService } from '../transfer';
import { STATE_SELECTORS } from '../state-service';
import { XprtDataService } from '../xprt-data';
import { StandWorksheetService } from '../stand-worksheet';
import { incompleteTransferModalDefinition } from './modal.definition';
import { PermissionService } from '../session/permission.service';
import { isNeedDMSync } from '@app/shared/utils';
import { DmDataService } from '../dm-data';
import { OPEN_AND_PENDING_STATUS } from '../transfer/transfer-constants';
import { EVENT_SELECTOR_SELECT } from '@config/global-constants';
import { I18n } from '@app/i18n/strings-keys';

const entityType: ENTITY_TYPES = ENTITY_TYPES.INCOMPLETE_TRANSFER;
const transferServiceConfig: IEntityConfig = {
  ENTITY_TYPE: entityType,
  entityNameToCheckPermissions: transferEntityType,
  isMasterListAvailable: true,
  needConvertTimeZoneFields: ['transaction_date'],
  buttons: {},
  editor: {
    entityTitle: getSingularEntityName(entityType),
  },
  apiEndpoint: transferEntityType,
  uiRoute: entityType,
  isFormServiceSupported: false,
  list: {
    gridOptions: {
      valueField: '_id',
      multiselect: true,
    },
    userGridOptions: {
      rowClassRules: {
        'xvi-grid-warn-row': (params: CellClassParams): boolean => {
          return isNeedDMSync(params?.data);
        },
      },
    },
    documentQuery: {
      select: ['updated_items', 'unavailable_items'],
      orderby: 'internal_number asc',
      notNeedActive: true,
    },
    documentOperationOptions: {},
    columns: [],
    titleObs: new BehaviorSubject(null),
    warningMessage$: new BehaviorSubject(null),
    titleTooltipIconConfig$: new BehaviorSubject({
      tooltipConfig: {
        tooltip: I18n.InfoMessages.WillRemoveTransferOnceSubmitted,
      },
    }),
  },
};

/**
 * Incomplete Transfer service that extends BaseService to use
 * basic CRUD request operations
 */
@Injectable()
export class IncompleteTransferService extends BaseService {
  public static config: IEntityConfig = transferServiceConfig;
  public static ENTITY_TYPE: string = transferServiceConfig.ENTITY_TYPE;
  public iframeMode: boolean = this.stateService.getBySelector(
    STATE_SELECTORS.IFRAME_MODE,
  );

  @lazyInject(Router) public router: Router;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(NgRedux) public ngRedux: NgRedux<IAppState>;
  @lazyInject(XprtDataActions) public xprtDataActions: XprtDataActions;
  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;
  @lazyInject(EventsActions) public eventsActions: EventsActions;
  @lazyInject(TransferService) public transferService: TransferService;
  @lazyInject(PermissionService) public permissionService: PermissionService;
  @lazyInject(StandWorksheetService)
  public standWorksheetService: StandWorksheetService;
  @lazyInject(DmDataService) public dmDataService: DmDataService;
  @select(['events', 'incompleteTransferSelectedEvents'])
  public selectedEvents$: Observable<IProcessedEvent[]>;
  @select(['tools', 'history']) public history$: Observable<string[]>;

  public gridListDataChanged$: Subject<XuxGridAction> = new Subject();

  public needGetUpdateItemsStatus: TRANSFER_STATUS_VALUE[] =
    OPEN_AND_PENDING_STATUS;

  public quickFilterFields: string[] = ['name'];

  public listProjection: string[] = ['name'];

  public headerNames: ColDef[] = [
    {
      headerName: 'Name',
      field: 'name',
      filter: false,
    },
  ];
  public xuxListContainer: XUXListContainerComponent;
  public beforeStartListMethods: Function[] = [
    this.hookXuxListContainer,
    this.initSubscriptions,
    this.fetchEventData,
    this.getEventAndInitTransfer,
  ];

  public constructor(public injector: Injector) {
    super(injector);

    this.config.buttons = {
      select_event: {
        ...BTN_SELECT_EVENT,
        class: 'btn btn-primary',
        click: this.selectEventBtnClickHandler,
      },
      edit: {
        ...BTN_EDIT,
        hidden: this.getIsHiddenStatusChangeBtnFn(null, true).bind(this),
      },
      create: {
        hidden: true,
      },
      submit: {
        value: 'submit',
        caption: I18n.Common.Submit,
        hidden: this.getIsHiddenStatusChangeBtnFn(
          TRANSFER_STATUS_VALUE.Open,
        ).bind(this),
        click: this.transferService
          .getBulkUpdateStatusFn(
            'submit',
            this.i18nService.translate(I18n.Status.submitted),
          )
          .bind(this),
      },
      cancel: {
        value: 'cancel',
        caption: I18n.Forms.TransferForm.CancelTransfer,
        hidden: this.getIsHiddenStatusChangeBtnFn(
          TRANSFER_STATUS_VALUE.Open,
        ).bind(this),
        click: this.transferService
          .getBulkUpdateStatusFn(
            'cancel',
            this.i18nService.translate(I18n.Status.canceled),
          )
          .bind(this),
      },
      accept: {
        value: 'accept',
        caption: I18n.Common.Accept,
        hidden: this.getIsHiddenStatusChangeBtnFn(
          TRANSFER_STATUS_VALUE.Pending,
        ).bind(this),
        click: this.transferService
          .getBulkUpdateStatusFn(
            'accept',
            this.i18nService.translate(I18n.Status.accepted),
          )
          .bind(this),
      },
      reject: {
        value: 'reject',
        caption: I18n.Common.Reject,
        hidden: this.getIsHiddenStatusChangeBtnFn(
          TRANSFER_STATUS_VALUE.Pending,
        ).bind(this),
        click: this.transferService
          .getBulkUpdateStatusFn(
            'reject',
            this.i18nService.translate(I18n.Status.rejected),
          )
          .bind(this),
      },
    };

    this.config.list.eventHandlers = {
      [XUX_GRID_ACTIONS.ITEMS_ARRAY_CHANGED]: (action): void =>
        this.gridListDataChanged$.next(action),
    };
    this.config.list.documentOperationOptions = {
      continueFetchCondition: (): boolean => {
        const state: IAppState = this.ngRedux.getState();

        return !isNil(state.events.incompleteTransferSelectedEvents);
      },
    };
    this.config.list.columns.push(
      ...[
        {
          path: 'transaction_date',
          title: I18n.Common.Date,
          filterType: CELL_FILTER.dateLocale,
          sortable: true,
          defaultView: true,
        },
        {
          path: 'internal_number',
          title: I18n.Forms.GridForm.ID,
          filterType: 'stringFilter',
          filterParams: {
            quickFilter: true,
          },
          sortable: true,
          defaultView: true,
        },
        {
          path: 'site_name',
          title: I18n.Forms.TransferForm.FromName,
          filterType: 'stringFilter',
          filterParams: {
            quickFilter: true,
          },
          cellRenderer: CELL_RENDERER.link_renderer,
          cellRendererParams: {
            entityType,
            needGetUpdateItemsStatus: OPEN_AND_PENDING_STATUS,
          },
          sortable: true,
          defaultView: true,
        },
        {
          path: 'store_number',
          title: I18n.Forms.TransferForm.FromID,
          filterType: 'stringFilter',
          filterParams: {
            quickFilter: true,
          },
          cellRenderer: CELL_RENDERER.link_renderer,
          cellRendererParams: {
            entityType,
            needGetUpdateItemsStatus: OPEN_AND_PENDING_STATUS,
          },
          sortable: true,
          defaultView: true,
        },
        {
          path: 'site_name_to',
          title: I18n.Forms.TransferForm.ToName,
          filterType: 'stringFilter',
          filterParams: {
            quickFilter: true,
          },
          cellRenderer: CELL_RENDERER.link_renderer,
          cellRendererParams: {
            entityType,
            needGetUpdateItemsStatus: OPEN_AND_PENDING_STATUS,
          },
          sortable: true,
          defaultView: true,
        },
        {
          path: 'store_number_to',
          title: I18n.Forms.TransferForm.ToID,
          filterType: 'stringFilter',
          filterParams: {
            quickFilter: true,
          },
          cellRenderer: CELL_RENDERER.link_renderer,
          cellRendererParams: {
            entityType,
            needGetUpdateItemsStatus: OPEN_AND_PENDING_STATUS,
          },
          sortable: true,
          defaultView: true,
        },
        {
          path: 'transfer_type',
          title: I18n.Common.Type,
          filterType: CELL_FILTER.enum,
          filterParams: {
            values: TRANSFER_FILTER_STATUS_OPTIONS,
          },
          sortable: true,
          valueFormatter: (params) =>
            transferTypeFormatter(params, { i18nService: this.i18nService }),
          defaultView: true,
        },
        {
          path: 'event_id',
          title: I18n.EntityNames.Event,
          defaultView: true,
        },
        {
          path: 'status',
          title: I18n.Common.Status,
          sortable: true,
          defaultView: true,
          cellRenderer: CELL_RENDERER.prepared_string_renderer,
          cellRendererParams: {
            valueToEnumMap: TRANSFER_STATUS_ENUM_OPTIONS,
          },
        },
        {
          path: COLUMN_SELECTOR_ID,
          width: 50,
          title: '',
          defaultView: true,
          cellRenderer: CELL_RENDERER.incomplete_transfer_actions_renderer,
          pinned: detectMobile() ? false : 'right',
          lockPinned: true,
        },
      ],
    );
  }

  public get isCanManage(): boolean {
    return this.permissionService.canManage(TransferService.ENTITY_TYPE);
  }

  public getEventAndInitTransfer(): void {
    this.subscriptions.push(
      this.history$
        .pipe(
          rx.mergeMap<
            string[],
            Observable<{ selectedEvent: IEvent; action: string }>
          >((history: string[]) => {
            const incompleteTransferFlag = 'incomplete-transfer?';
            if (
              history.length >= 2 &&
              includes(nth(history, 1), incompleteTransferFlag)
            ) {
              const pageUri = last(
                split(nth(history, 1), incompleteTransferFlag),
              );
              const params = new URLSearchParams(pageUri);
              const eventId = params.get('eventId');
              const action = params.get('action');
              if (eventId && action) {
                return this.xprtDataService.getEventDetailById(eventId).pipe(
                  rx.map((selectedEvent: IEvent) => ({
                    selectedEvent,
                    action,
                  })),
                );
              }
            }

            return of();
          }),
        )
        .subscribe(({ selectedEvent, action }) => {
          const processEventsList = this.xprtDataService.processEventsList([
            selectedEvent,
          ]);
          const type: FilterOptionType = 'containsAny';
          if (isEqual(action, 'open')) {
            this.uiListParams = {
              filterModel: {
                transfer_type: {
                  type,
                  filter: [TRANSFER_TYPE.PRE_EVENT_TRANSFER],
                  headerText: this.i18nService.translate(
                    I18n.Forms.TransferForm.PreEventHyphen,
                  ),
                },
              },
            };
          } else if (isEqual(action, 'close')) {
            this.uiListParams = {
              filterModel: {
                transfer_type: {
                  type,
                  filter: [
                    TRANSFER_TYPE.DURING_EVENT_TRANSFER,
                    TRANSFER_TYPE.OUTSIDE_OF_EVENT_TRANSFER,
                  ],
                  headerText: [
                    this.i18nService.translate(
                      I18n.Forms.TransferForm.DuringEvent,
                    ),
                    this.i18nService.translate(
                      I18n.Forms.TransferForm.OutsideofEvent,
                    ),
                  ].join(),
                },
              },
            };
          } else {
            this.uiListParams = {};
          }
          this.eventsActions.saveIncompleteTransferEvents(
            head(processEventsList),
          );
        }),
    );
  }

  public getIsHiddenStatusChangeBtnFn(
    targetStatus?: TRANSFER_STATUS_VALUE,
    isSingleSelection: boolean = false,
  ): Function {
    return (selectedItems: IBaseEntityType[]): boolean => {
      const isNotOnlyTargetStatusItems = (): boolean =>
        isEmpty(targetStatus)
          ? false
          : some(selectedItems, (item) => !isEqual(targetStatus, item.status));

      const selectedItemLength = size(selectedItems);
      const isSelectedItemInvalid = isSingleSelection
        ? selectedItemLength !== 1
        : selectedItemLength < 1;

      return (
        !this.isCanManage ||
        isSelectedItemInvalid ||
        isNotOnlyTargetStatusItems()
      );
    };
  }

  public hookXuxListContainer(
    xuxListContainerComponent: XUXListContainerComponent,
  ): void {
    this.xuxListContainer = xuxListContainerComponent;
  }

  public selectEventBtnClickHandler = async (): Promise<void> => {
    let result: IProcessedEvent[] | IModalDialogResult;
    if (this.iframeMode) {
      const state: IAppState = this.ngRedux.getState();
      const storedEventId = get(
        state,
        'events.incompleteTransferSelectedEvents.id',
        '',
      );

      let filteredEventIds: string[];
      if (storedEventId) {
        try {
          this.uiActions.enableLoader();
          filteredEventIds = await this.xprtDataService
            .getEventIdsByFilter({
              status: [EVENT_STATUS_VALUE.Open, EVENT_STATUS_VALUE.Pending],
              ids: [storedEventId],
            })
            .toPromise();
          this.uiActions.disableLoader();
        } catch (error) {
          this.uiActions.disableLoader();
        }
      }
      result = await this.xprtDataActions.showEventSelector({
        selectionLimit: 1,
        defaultSelectedEvents: filteredEventIds || [],
        select: EVENT_SELECTOR_SELECT,
        gridExtraFilters: [`status eq in(open,pending)`],
        defaultSort: 'status desc,event_days/start_date_time desc',
      });
      if (isEqual(result.modalResult, CANCEL_ACTION)) {
        return;
      }
      const processEventsList = this.xprtDataService.processEventsList(
        result.items,
      );
      const selectedEvent: IProcessedEvent = head(processEventsList);
      this.eventsActions.saveIncompleteTransferEvents(selectedEvent);

      return;
    }
    result = await this.openEventSelectorModal();
    if (!result.confirmed) return;
    const selectedEventResult: IProcessedEvent = head(result.value);
    this.eventsActions.saveIncompleteTransferEvents(selectedEventResult);

    return;
  };

  public async openEventSelectorModal(): Promise<IModalDialogResult> {
    const state: IAppState = this.ngRedux.getState();
    const modalOptions =
      incompleteTransferModalDefinition.incompleteTransferEventSelectorModal(
        state,
        [EVENT_STATUS_VALUE.Open, EVENT_STATUS_VALUE.Pending],
      );
    const dialogResult = await this.uiActions.promptFormModal(modalOptions);

    return {
      confirmed: dialogResult.confirmed,
      modalResult: dialogResult.modalResult,
      value: dialogResult.value.events,
    };
  }

  public fetchEventData(): void {
    this.standWorksheetService.fetchDataForEventSelector();
  }

  public initSubscriptions(): void {
    this.selectedEventChangedSubs();
    this.gridAndSelectedEventChangedSubs();
  }

  public gridAndSelectedEventChangedSubs(): void {
    const subs = observableCombineLatest([
      this.selectedEvents$,
      this.gridListDataChanged$,
    ])
      .pipe(
        rx.distinctUntilChanged(isEqual),
        rx.tap(([selectedEvent, gridDataChangeAction]) => {
          this.checkAndShowGridWarningMessage(
            get(
              gridDataChangeAction,
              ['payload', 'rowData'],
              [],
            ) as ITransfer[],
            selectedEvent,
          );
        }),
      )
      .subscribe(() => {});

    this.subscriptions.push(subs);
  }

  public selectedEventChangedSubs(): void {
    this.subscriptions.push(
      this.selectedEvents$
        .pipe(rx.distinctUntilChanged(isEqual))
        .subscribe(this.handleSelectedEventChange.bind(this)),
    );
  }

  public async handleSelectedEventChange(
    selectedEvent: IProcessedEvent,
  ): Promise<void> {
    // just return when selectedEvent is nil
    if (isNil(selectedEvent)) {
      return;
    }

    this.uiActions.enableLoader();

    try {
      const selectedEventId = selectedEvent.id;

      const optionsBuilderMapping = {
        [EVENT_STATUS_VALUE.Open]: async (): Promise<IRequestOptions> =>
          this.buildOpenEventTransferOptions(selectedEventId),
        [EVENT_STATUS_VALUE.Pending]: async (): Promise<IRequestOptions> =>
          Promise.resolve(
            this.buildPendingEventTransferOptions(selectedEventId),
          ),
      };

      const listQueryOptions: IRequestOptions =
        await optionsBuilderMapping[selectedEvent.status]();

      this.config.list.titleObs.next(
        `${selectedEvent.name} - ${selectedEvent.date}`,
      );
      this.config.list.documentQuery = {
        ...this.config.list.documentQuery,
        filter: listQueryOptions.filter,
      };

      this.config.list.documentOperationOptions.headers = merge(
        {},
        this.config.list.documentOperationOptions?.headers,
        listQueryOptions.headers,
      );

      // delete XSiteGroupId if listQueryOptions.headers not have it
      if (
        isNil(listQueryOptions.headers?.[EHeaderName.XSiteGroupId]) &&
        this.config.list.documentOperationOptions.headers
      ) {
        delete this.config.list.documentOperationOptions.headers[
          EHeaderName.XSiteGroupId
        ];
      }

      this.xuxListContainer.updateListViewData(
        merge({}, this.uiListParams, {
          currentPage: 1,
        }),
      );
      this.uiListParams = {};
    } finally {
      this.uiActions.disableLoader();
    }
  }

  /**
   * load pending during event & outside event filter & headers
   *
   * @param eventId target event id
   * @returns [filter, outside event headers]
   */
  public async buildOpenEventTransferOptions(
    eventId: string,
  ): Promise<IRequestOptions> {
    if (isEmpty(eventId)) {
      return;
    }

    const duringEventFilter = this.buildEventTransferFilter(
      eventId,
      TRANSFER_TYPE.DURING_EVENT_TRANSFER,
    );
    const outsideEventHeaders =
      await this.buildOutsideEventTransferHeaders(eventId);
    const outsideEventFilter = `transfer_type eq '${TRANSFER_TYPE.OUTSIDE_OF_EVENT_TRANSFER}' and (site_id eq in({siteGroup}) or site_id_to eq in({siteGroup}))`;

    const combinedFilter = `(${duringEventFilter}) or (${outsideEventFilter})`;

    return {
      filter: `status eq '${TRANSFER_STATUS_VALUE.Pending}' and (${combinedFilter})`,
      headers: outsideEventHeaders,
    };
  }

  /**
   * load pre event transfer filter
   *
   * @param eventId target event id
   */
  public buildPendingEventTransferOptions(eventId: string): IRequestOptions {
    if (isEmpty(eventId)) {
      return;
    }

    return {
      filter: `status eq '${TRANSFER_STATUS_VALUE.Open}' and (${this.buildEventTransferFilter(
        eventId,
        TRANSFER_TYPE.PRE_EVENT_TRANSFER,
      )})`,
    };
  }

  public buildEventTransferFilter(
    eventId: string,
    transferType: TRANSFER_TYPE,
  ): string {
    if (isEmpty(eventId) || isEmpty(transferType)) {
      return;
    }

    return `event_id eq objectid(${eventId}) and transfer_type eq '${transferType}'`;
  }

  public async buildOutsideEventTransferHeaders(
    eventId: string,
  ): Promise<DocumentOperationHeaders> {
    const headers: DocumentOperationHeaders = {};
    if (isEmpty(eventId)) {
      return headers;
    }

    const eventAvailabilitySiteGroupId =
      await this.getEventAvailabilitySiteGroupId(eventId);

    if (!isEmpty(eventAvailabilitySiteGroupId)) {
      headers[EHeaderName.XSiteGroupId] = eventAvailabilitySiteGroupId;
    }

    return headers;
  }

  public async getEventAvailabilitySiteGroupId(
    eventId: string,
  ): Promise<string> {
    if (isEmpty(eventId)) {
      return;
    }

    const availabilitySiteIds = await this.getEventAvailabilitySiteIds(eventId);

    if (isEmpty(availabilitySiteIds)) {
      return;
    }

    return this.dmDataService.getSiteGroupId(availabilitySiteIds).toPromise();
  }

  public async getEventAvailabilitySiteIds(eventId: string): Promise<string[]> {
    if (isEmpty(eventId)) {
      return;
    }

    const eventDetail = await this.xprtDataService
      .getEventDetailById(eventId, {
        filter_by_assigned_sites: true,
        include_available_sites: true,
        available_sites_select: 'id,name,store_number',
        select: ['available_sites'],
      })
      .toPromise();

    return map(get(eventDetail, 'available_sites', []), 'id');
  }

  public fetchList(
    userQuery?: IFilterQuery,
    options: IDocumentOperationOptions = {},
    skipRequestEvent: boolean = false,
  ): Observable<any> {
    return this.transferService.fetchList(userQuery, options, skipRequestEvent);
  }

  public checkAndShowGridWarningMessage(
    rowData: ITransfer[],
    selectedEvent: IProcessedEvent,
  ): void {
    let hasDmSyncData = false;

    if (!isEmpty(rowData)) {
      hasDmSyncData = some(rowData, (transfer) => isNeedDMSync(transfer));
    }

    this.config.list.warningMessage$.next(
      hasDmSyncData
        ? this.i18nService.translate(
            I18n.InfoMessages.PleaseReviewDMBeforeDoing,
            {
              doing: isEqual(EVENT_STATUS_VALUE.Open, selectedEvent.status)
                ? this.i18nService.translate(
                    I18n.Forms.GridForm.AcceptingOrRejecting,
                  )
                : this.i18nService.translate(I18n.Forms.GridForm.Submitting),
            },
          )
        : '',
    );
  }
}
